import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { useTranslation } from 'react-i18next';
import { Container } from '@components/atoms/Container';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Form } from '@components/atoms/Form';
import { InputErrors } from '@types';
import { formatCarPlate, formatSeatCount, isValidPlate } from '../../../utils/validation';
import { useCarInfoRequest } from '@services/useCarInfoRequest';
import { useCreateVehicleRequest } from '@services/useCreateVehicleRequest';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import styles from './EditVehicleData.module.css';

interface EditVehicleFormErrors {
    brand: InputErrors;
    model: InputErrors;
    color: InputErrors;
    plate: InputErrors;
    seats: InputErrors;
}

type Vehicle = {
    uuid?: string;
    brand?: string;
    model?: string;
    color?: string;
    plate?: string;
    seats?: number;
    is_primary?: boolean;
};

export const EditVehicleData: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, setUser } = useData();
    const { showDefaultNotification } = useNotifications();

    const location = useLocation();
    const vehicleUuid: string | undefined = (location.state as { vehicleUuid?: string })?.vehicleUuid;

    const isCreateMode = !vehicleUuid;
    const isEditMode = Boolean(vehicleUuid);

    const vehicles: Vehicle[] = user?.data?.vehicles ?? [];

    const vehicleToEdit = isEditMode ? vehicles.find((v) => v.uuid === vehicleUuid) : undefined;

    const [brand, setBrand] = useState(isCreateMode ? '' : (vehicleToEdit?.brand ?? ''));
    const [model, setModel] = useState(isCreateMode ? '' : (vehicleToEdit?.model ?? ''));
    const [color, setColor] = useState(isCreateMode ? '' : (vehicleToEdit?.color ?? ''));
    const [plate, setPlate] = useState(isCreateMode ? '' : (vehicleToEdit?.plate ?? ''));
    const [seats, setSeats] = useState(
        isCreateMode ? '' : vehicleToEdit?.seats !== undefined ? vehicleToEdit.seats.toString() : ''
    );

    const [errors, setErrors] = useState<EditVehicleFormErrors>({
        brand: [],
        model: [],
        color: [],
        plate: [],
        seats: [],
    });

    const formRef = useRef<HTMLFormElement>(null);

    const { request: updateVehicle, error, validationErrors, lastResponseTime, success } = useCarInfoRequest();

    const {
        request: createVehicle,
        error: createError,
        validationErrors: createValidationErrors,
        lastResponseTime: createLastResponseTime,
        success: createSuccess,
    } = useCreateVehicleRequest();

    const translations = {
        title: isCreateMode ? t('user-area.add-vehicle') : t('user-area.edit-vehicle'),
        save: t('navigation.save'),
        brand: t('car-info-step.brand'),
        model: t('car-info-step.model'),
        color: t('car-info-step.color'),
        plate: t('car-info-step.plate'),
        seats: t('car-info-step.seats'),
        brandRequiredError: t('car-info-step.brand.required-error'),
        modelRequiredError: t('car-info-step.model.required-error'),
        colorRequiredError: t('car-info-step.color.required-error'),
        plateRequiredError: t('car-info-step.plate.required-error'),
        plateInvalidError: t('car-info-step.plate.invalid-error'),
        seatsRequiredError: t('car-info-step.seats.required-error'),
    };

    const validateForm = (): EditVehicleFormErrors => {
        const newErrors: EditVehicleFormErrors = {
            brand: [],
            model: [],
            color: [],
            plate: [],
            seats: [],
        };

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !brand.trim() && newErrors.brand.push(translations.brandRequiredError);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !model.trim() && newErrors.model.push(translations.modelRequiredError);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !color.trim() && newErrors.color.push(translations.colorRequiredError);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !plate.trim() && newErrors.plate.push(translations.plateRequiredError);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !seats.trim() && newErrors.seats.push(translations.seatsRequiredError);

        if (plate.trim() && !isValidPlate(plate.trim())) {
            newErrors.plate.push(translations.plateInvalidError);
        }

        setErrors(newErrors);
        return newErrors;
    };

    const handleSubmitClick = () => {
        const validation = validateForm();
        if (Object.values(validation).every((v) => v.length === 0)) {
            formRef.current?.requestSubmit();
        }
    };

    const submitForm = () => {
        if (!user?.data?.uuid) return;

        if (isCreateMode) {
            createVehicle({
                brand,
                model,
                color,
                plate,
                seats: Number(seats),
            });
        } else if (isEditMode && vehicleToEdit?.uuid) {
            updateVehicle(vehicleToEdit.uuid, {
                user_uuid: user.data.uuid,
                brand,
                model,
                color,
                plate,
                seats: Number(seats),
            });
        }
    };

    // EDIT
    useEffect(() => {
        if (!lastResponseTime) return;

        if (validationErrors) {
            setErrors({
                brand: validationErrors.brand || [],
                model: validationErrors.model || [],
                color: validationErrors.color || [],
                plate: validationErrors.plate || [],
                seats: validationErrors.seats || [],
            });
            return;
        }

        if (error) {
            showDefaultNotification({ type: 'error', message: error });
            return;
        }

        if (success && user && vehicleToEdit) {
            showDefaultNotification({ type: 'success', message: success });

            const updatedVehicles = vehicles.map((v) =>
                v.uuid === vehicleToEdit.uuid ? { ...v, brand, model, color, plate, seats: Number(seats) } : v
            );

            const updatedLastVehicle =
                user.data.last_vehicle?.uuid === vehicleToEdit.uuid
                    ? { ...user.data.last_vehicle, brand, model, color, plate, seats: Number(seats) }
                    : user.data.last_vehicle;

            setUser({
                ...user,
                data: {
                    ...user.data,
                    vehicles: updatedVehicles,
                    last_vehicle: updatedLastVehicle,
                },
            });

            navigate(-1);
        }
    }, [lastResponseTime]);

    // CREATE
    useEffect(() => {
        if (!createLastResponseTime) return;

        if (createValidationErrors) {
            setErrors({
                brand: createValidationErrors.brand || [],
                model: createValidationErrors.model || [],
                color: createValidationErrors.color || [],
                plate: createValidationErrors.plate || [],
                seats: createValidationErrors.seats || [],
            });
            return;
        }

        if (createError) {
            showDefaultNotification({ type: 'error', message: createError });
            return;
        }

        if (createSuccess && user) {
            showDefaultNotification({ type: 'success', message: createSuccess });

            const newVehicle = {
                uuid: crypto.randomUUID(),
                brand,
                model,
                color,
                plate,
                seats: Number(seats),
                is_primary: false,
            };

            setUser({
                ...user,
                data: {
                    ...user.data,
                    vehicles: [...vehicles, newVehicle],
                },
            });

            navigate(-1);
        }
    }, [createLastResponseTime]);

    return (
        <Container classes={styles.containerBackground}>
            <div className="d-flex align-items-center position-relative">
                <div className="position-absolute start-0">
                    <RoundIcon
                        classes={styles.roundIcon}
                        icon="fa-thin fa-arrow-left"
                        variant={RoundIconVariants.variant1}
                        onClick={() => navigate(-1)}
                    />
                </div>

                <Typography fontSize="24" fontWeight="800" classes="m-auto">
                    {translations.title}
                </Typography>
            </div>

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <hr />

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <Form formRef={formRef} onSubmit={submitForm}>
                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.text}
                    label={`${translations.brand}*`}
                    value={brand}
                    onChange={setBrand}
                    errorMessages={errors.brand}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.text}
                    label={`${translations.model}*`}
                    value={model}
                    onChange={setModel}
                    errorMessages={errors.model}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.text}
                    label={`${translations.color}*`}
                    value={color}
                    onChange={setColor}
                    errorMessages={errors.color}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.text}
                    label={`${translations.plate}*`}
                    value={plate}
                    maxLength={8}
                    placeholder="XX-XX-XX"
                    onChange={(value) => setPlate(formatCarPlate(value))}
                    errorMessages={errors.plate}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.text}
                    label={`${translations.seats}*`}
                    value={seats}
                    onChange={(value) => setSeats(formatSeatCount(value))}
                    errorMessages={errors.seats}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Button label={translations.save} mode={ButtonMode.primary} onClick={handleSubmitClick} classes="w-100" />
            </Form>
        </Container>
    );
};
